import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../utils/pricing';

// 紺帯番号バンド(home/page.tsx NavyBand と同一文法・移植)。帯=区切り(#31)／帯直後に同一文言見出しを反復しない(#33)。
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== フットマン詳細 全面最新化（Implementer A スコープ） =====
// 土台: about/overview + home の新スタイル。カタログ: Hero=BB-02 / 見出しH2-02系 / SEC-N02,N04,N07翻案。
// 事実正典（創作禁止）: 現行 working tree services/footman（旧実文の特徴・シーン・料金）+ butler-services hub料金表
//   + mng-thebutler/00_事業説明.md L75(フットマン=3時間スポット),L52-54(提供内容)。料金データ変更禁止。

export default function FootmanPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事サービス', href: '/butler-services/' },
    { label: 'フットマン' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: 'Flexible', ja: '柔軟な利用時間', d: '3時間からご利用可能で、時間単位の延長も承ります。午前中だけ、夕方のパーティーだけといった短時間から、丸一日のイベントサポートまで、ご予定に合わせて柔軟に対応します。' },
    { en: 'Certified', ja: '協会認定の執事', d: '対応するのは全日本執事協会が認定した執事のみ。130年以上の歴史が培った教育プログラムを修了し、実務経験を積んだプロフェッショナルだけがサービスを提供します。' },
    { en: 'Customized', ja: '完全カスタマイズ', d: 'パーティーの準備から片付け、来客対応、料理のサーブ、ドリンクサービスまで、ご要望に合わせて内容を柔軟に組み立てます。必要なサポートをすべてお任せください。' },
    { en: 'Free Selection', ja: '執事指名は無料', d: '過去にご利用いただいた執事や、執事名簿でご覧いただいた執事を無料で指名できます。お気に入りの執事を見つけて、継続的にご利用いただくことも可能です。' },
  ];

  const scenes = [
    { t: 'ホームパーティー・来客対応', d: 'ご自宅でのパーティーや大切なゲストをお招きする際、執事が料理のサーブ、ドリンクサービス、テーブルセッティング、片付けまでを担当。主催者であるお客様はゲストとの会話に専念でき、その場の格が一段と高まります。' },
    { t: 'ビジネスイベント・接待', d: '企業の重要な接待や社内イベントで、執事が会場設営、受付対応、ドリンクサービス、進行サポートを行います。VIPゲストへの細やかな配慮とスムーズな進行で、ビジネスにふさわしい品格ある対応を実現します。' },
    { t: '記念日・特別な日のサポート', d: '誕生日、結婚記念日、還暦祝いなど、人生の特別な日を執事とともに。サプライズの演出や特別な空間づくりのお手伝いで、忘れられない思い出を創ります。' },
    { t: '一時的な家事・撮影のサポート', d: '引っ越し後の整理整頓や大掃除、季節の衣替えなど集中して家事を行いたい時に。映像作品やCM撮影で本物の執事が登場するシーンにも、正統な所作でお応えします。' },
  ];

  const serviceGroups = [
    { t: 'パーティー・イベント関連', items: ['テーブルセッティング・デコレーション', '料理・ドリンクのサーブ', 'ゲスト対応・誘導', 'グラスの管理・交換', '会場の整理・片付け', 'クローク対応'] },
    { t: '日常生活サポート', items: ['食事の準備・配膳', '清掃・整理整頓', '洗濯・アイロンがけ', '買い物代行', 'ペットのケア', '郵便物の整理'] },
    { t: 'ビジネス関連', items: ['来客対応・受付', '会議室の準備・片付け', 'ドリンクサービス', '資料の整理・配布', 'VIPゲストのエスコート'] },
    { t: 'その他', items: ['通院・外出の付き添い', '荷物の運搬・整理', '贈り物の手配・ラッピング', '特別な演出のサポート'] },
  ];

  const faqs = [
    ['3時間で何ができますか？', 'ホームパーティーの準備・サーブ・片付け、来客対応と食事サービス、家事の集中サポートなど、様々なことが可能です。ご要望に合わせて内容を組み立てます。'],
    ['執事を指名することはできますか？', 'はい、無料で指名いただけます。執事名簿からお選びいただくか、過去にご利用いただいた執事をご指名ください。'],
    ['当日の延長は可能ですか？', `執事のスケジュールが空いている場合は可能です。延長料金は${yenInc(jbaDispatchExTax.footmanExtension)}円/時間（税込）となります。`],
    ['サービス提供エリアは？', '主に東京都内および近郊エリアとなります。その他の地域はご相談ください。'],
    ['どのような執事が来ますか？', '全日本執事協会で教育を受け、認定試験に合格した経験豊富な執事のみが対応します。'],
  ];

  const related = [
    { name: 'Butler', ja: 'バトラー', cad: '1日8時間の専属サービス', price: yenInc(jbaDispatchExTax.butlerBase), unit: '円〜（税込）', href: '/services/butler/' },
    { name: 'Second Steward', ja: 'セカンドスチュワード', cad: '1ヶ月20日間の継続利用', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '円〜（税込）', href: '/services/second-steward/' },
    { name: 'House Steward', ja: 'ハウススチュワード', cad: '24時間・3人交代の常駐型', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: '円（税込）', href: '/services/house-steward/' },
  ];

  return (
    <Layout>
      <title>フットマン｜3時間からの執事スポット利用と料金 | 全日本執事協会 THE BUTLER</title>
      <meta name="description" content={`THE BUTLER のフットマンは、プロの執事を3時間から気軽にご利用いただけるスポット型サービス。ホームパーティー・来客対応・イベント給仕などに。料金${yenInc(jbaDispatchExTax.footmanBase)}円（税込・3時間）から、執事指名は無料です。`} />
      <link rel="canonical" href="https://butler-association.jp/services/footman" />

      {/* ヒーロー BB-02 */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-p-042-svc-footman-w2.webp" alt="フットマンサービス。品格ある執事が来客やパーティーを支える様子" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[700px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan Ⅰ — Footman</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(27px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">3時間から、</span>
              <span className="block">執事を気軽に</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              フットマンは、プロフェッショナルな執事を3時間から気軽にご利用いただける、最も手軽な入口のプランです。ホームパーティー、ビジネスイベント、来客対応、日常生活の一時的なサポートまで、経験豊富な執事が高品質なおもてなしを提供します。まずは「執事を体験してみたい」というお客様に最適です。
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="このサービスについて" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 特徴 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              手軽さの中にある、<span className="text-[#a38e65]">本物の品格</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>フットマンは、全日本執事協会で厳格な教育を受け、実務経験を積んだ認定執事が、ご要望に合わせた質の高いサービスを提供する、最も手軽にご利用いただける執事人材サービスです。</p>
              <p>執事という職業は、単なる家事代行やアシスタントとは異なり、お客様の立場に立った先回りの気配り、状況判断力、そして品格ある立ち居振る舞いが求められます。一般的なサービススタッフでは実現できない、洗練されたおもてなしをご体験いただけます。</p>
              <p>特に、ホームパーティーやビジネスイベントでは、執事の存在がその場の格を一段と高めます。ゲストへの細やかな配慮、スムーズな進行サポート、控えめながらも確実な対応により、主催者であるお客様は安心してその場を楽しむことができます。</p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.ja} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="ご利用シーン" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 ご利用シーン（画像split） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              フットマンが活躍する場面
            </h2>
            <div className="grid lg:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start">
              <img src="/images/generated/jba-space-hall-03.webp" alt="来客を迎えるサロンの広間" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30" />
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="提供サービス" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 提供サービス内容 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              提供できる主なサービス
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              ご要望に応じて、以下のようなサービスを組み合わせて提供します。記載以外のご要望についても、可能な限り対応いたしますのでお気軽にご相談ください。
            </p>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-9">
              {serviceGroups.map((g) => (
                <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4">{g.t}</h3>
                  <ul className="space-y-2.5">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">
                        <span className="mt-2 w-1.5 h-1.5 bg-[#a38e65] shrink-0"></span>{it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="料金" en="Pricing" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 料金（22-26px級serif強調） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              明確でわかりやすい料金
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[42px] text-[#f8f6f0] leading-none">{yenInc(jbaDispatchExTax.footmanBase)}<span className="text-lg text-[#d7b779] ml-1">円</span></div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">税込・3時間</p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['基本料金', `3時間：${yenInc(jbaDispatchExTax.footmanBase)}円（税込）`],
                    ['延長料金', `${yenInc(jbaDispatchExTax.footmanExtension)}円/時間（税込）`],
                    ['執事指名', '無料'],
                    ['交通費', '実費精算'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em]">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[17px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-6 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">
                  <div><span className="text-[#8f7a56]">別途費用：</span>交通費（実費）、食材費・備品購入費（実費）、特殊な機材が必要な場合</div>
                  <div><span className="text-[#8f7a56]">お支払い：</span>銀行振込、クレジットカード。キャンセルは7日前まで無料／3〜6日前30%／前日〜当日100%</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="よくあるご質問" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくあるご質問
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="その他のプラン" en="Other Plans" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 関連サービス（3カード＋一覧ボタン） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              より長時間・長期間のプラン
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              フットマンで執事を体験されたあとは、必要に応じて上位のプランへ。関わりが深まるほど、執事はお客様を深く理解します。
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <span className="block font-['Playfair_Display',serif] text-[13px] tracking-[0.1em] text-[#8f7a56] mb-1">{r.name}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/butler-services/" className={BTN08_ON_LIGHT}>執事サービス一覧へ戻る</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">フットマンのご利用・ご相談はこちら</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                ご希望の日時・場所・サービス内容をお知らせください。専門スタッフが詳しくお伺いし、最適なプランをご提案します。お見積りは無料です。
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせフォームへ</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">お電話：03-6824-4086（平日10:00-18:00）</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
